/** Pure helpers for multi-select lists: toggling, shift-click ranges and batched fan-out. */

export function toggleId(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Shift-click semantics: everything between the anchor and the target (inclusive, in
 * display order) is added to the selection. Falls back to a plain toggle when the
 * anchor is unknown or no longer on screen.
 */
export function selectRange(
  selected: ReadonlySet<string>,
  order: readonly string[],
  anchorId: string | null,
  targetId: string,
): Set<string> {
  const from = anchorId ? order.indexOf(anchorId) : -1;
  const to = order.indexOf(targetId);
  if (from === -1 || to === -1) return toggleId(selected, targetId);
  const next = new Set(selected);
  const [start, end] = from < to ? [from, to] : [to, from];
  for (let i = start; i <= end; i++) next.add(order[i]!);
  return next;
}

/** Runs `fn` over `items` with at most `limit` in flight, preserving input order. */
export async function runBatched<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const index = cursor++;
      try {
        results[index] = { status: "fulfilled", value: await fn(items[index]!) };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return results;
}
